import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  Users,
  ChevronLeft,
  ChevronRight,
  Download,
  Plus,
} from 'lucide-react';
import { useJobs, useJobStats, useUpdateJob } from 'features/job/hooks/useJobs';
import { usePrefetchJobs } from 'features/job/hooks/usePrefetchJobs';
import { useURLSync } from 'features/job/hooks/useURLSync';
import { ViewJobModal } from 'features/job/components/ViewJobModal';
import { DeleteConfirmModal } from 'features/job/components/DeleteConfirmModal';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { jobServices } from 'features/job/services/job.service';

const JobList = () => {
  const navigate = useNavigate();
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchJobs();

  // URL Sync for filters
const [filters, setFilters] = useURLSync({
  page: '1',
  limit: '10',
  status: 'all',
  visibility: 'all',
  search: '',
  sortBy: 'createdAt',
  order: 'desc',
});

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Search state with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch data
  const { data: response, isPending, isError, error } = useJobs(filters);
  const { data: statsData } = useJobStats();

  const jobs = response?.data?.jobs || [];
  const pagination = response?.data?.pagination || {};
  const stats = statsData?.data || {};
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();

const handleExport = async () => {
  try {
    const response =
      await jobServices.exportJobs(
        filters
      );

    const blob =
      new Blob(
        [response.data],
        {
          type: 'text/csv',
        }
      );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement(
        'a'
      );

    link.href = url;

    link.download =
      `jobs-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export jobs',
      error
    );
  }
};
   
  // Prefetch optimization
  useEffect(() => {
    if (pagination) {
      const { page: currentPage, totalPages } = pagination;
      const pageNum = parseInt(currentPage);
      if (pageNum < totalPages) {
        prefetchNextPage(filters, totalPages);
      }
      if (pageNum > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [pagination, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  const handlePageChange = (newPage) => {
    updateFilter('page', String(newPage));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (column) => {
  const newOrder =
    filters.sortBy === column &&
    filters.order === "asc"
      ? "desc"
      : "asc";

  setFilters((prev) => ({
    ...prev,
    sortBy: column,
    order: newOrder,
    page: "1",
  }));
};

const SortIcon = ({ column }) => {
  if (filters.sortBy !== column) {
    return (
      <span className="text-gray-400">
        ↕
      </span>
    );
  }

  return filters.order === "asc"
    ? <span>↑</span>
    : <span>↓</span>;
};

  const handleStatusToggle = (job) => {
  const newStatus = job.status === 'Active' ? 'Inactive' : 'Active';
  updateJob({ jobId: job._id, data: { status: newStatus } });
};

  const handleEdit = (job) => {
    navigate(`/jobs/${job._id}/edit`);
  };

  const handleView = (job) => {
    setSelectedJob(job);
    setIsViewModalOpen(true);
  };

  

  const handleDelete = (job) => {
    setSelectedJob(job);
    setIsDeleteModalOpen(true);
  };

  const handleViewApplicants = (job) => {
    navigate(`/jobs/${job._id}/applicants`);
  };

  const getStatusColor = (status) => {
    const colors = {
      Active: 'bg-green-500/10 text-green-600 dark:text-green-400',
      Inactive: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      Closed: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      Public: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      Private: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    };
    return (
      colors[visibility] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    );
  };

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>
            Error loading jobs
          </div>
          <p className='text-muted-foreground'>
            {error?.message || 'Something went wrong'}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`p-6 space-y-6 ${PAGE_CONTENT_WIDTH}`}>
        {/* Header */}
        <PageHeader
          icon={Briefcase}
          title="Job Management"
          subtitle="Manage job postings and applications"
          actions={
            !isError && (
              <div className='flex items-center gap-3'>
                <button
                  onClick={() => navigate('/jobs/create')}
                  className='flex items-center space-x-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm font-medium'
                >
                  <Plus size={16} />
                  <span>Create Job</span>
                </button>
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              </div>
            )
          }
        />

        {/* Stats Cards */}
        {stats && (
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='bg-card border border-border rounded-lg p-4 item-box'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-muted-foreground'>Total Jobs</p>
                  <p className='text-2xl font-bold text-foreground'>
                    {stats.totalJobs || 0}
                  </p>
                </div>
                <Briefcase size={24} />
              </div>
            </div>
            <div className='bg-card border border-border rounded-lg p-4 item-box'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-muted-foreground'>Active Jobs</p>
                  <p className='text-2xl font-bold text-green-600 dark:text-green-400'>
                    {stats.activeJobs || 0}
                  </p>
                </div>
                <Briefcase size={24} />
              </div>
            </div>
            <div className='bg-card border border-border rounded-lg p-4 item-box'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-muted-foreground'>
                    Total Applications
                  </p>
                  <p className='text-2xl font-bold text-purple-600 dark:text-purple-400'>
                    {stats.totalApplications || 0}
                  </p>
                </div>
                <Users size={24} />
              </div>
            </div>
            {/* <div className='bg-card border border-border rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-muted-foreground'>
                    Pending Applications
                  </p>
                  <p className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                    {stats.pendingApplications || 0}
                  </p>
                </div>
                <Users className='text-yellow-500' size={24} />
              </div>
            </div> */}
          </div>
        )}

        {/* Filters */}
        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            {/* Search */}
            <div className='md:col-span-2'>
              <div className='relative'>
                <Search
                  className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                  size={18}
                />
                <input
                  type='text'
                  placeholder='Search by job title, city...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className='w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className='w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
              >
                <option value='all'>All Status</option>
                <option value='Active'>Active</option>
                <option value='Inactive'>Inactive</option>
                <option value='Closed'>Closed</option>
              </select>
            </div>

            {/* Visibility Filter */}
            {/* <div>
              <select
                value={filters.visibility}
                onChange={(e) => updateFilter('visibility', e.target.value)}
                className='w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
              >
                <option value='all'>All Visibility</option>
                <option value='Public'>Public</option>
                <option value='Private'>Private</option>
              </select>
            </div> */}
          </div>
        </div>

        {/* Table */}
        <div className='bg-card border border-border rounded-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-muted/50 border-b border-border'>
                <tr>
                  {/* <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Job Title
                  </th> */}

                  <th
  className="text-left px-4 py-3 text-sm font-semibold text-foreground cursor-pointer"
  onClick={() => handleSort("jobTitle")}
>
  <div className="flex items-center gap-1">
    Job Title
    <SortIcon column="jobTitle" />
  </div>
</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Posted By
                  </th>
                   <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Ph No.(Posted By)
                  </th>
                  {/* <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Location
                  </th> */}

                  <th
  className="text-left px-4 py-3 text-sm font-semibold text-foreground cursor-pointer"
  onClick={() => handleSort("address.city")}
>
  <div className="flex items-center gap-1">
    Location
    <SortIcon column="address.city" />
  </div>
</th>
                  {/* <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Deadline
                  </th> */}

                  <th
  className="text-left px-4 py-3 text-sm font-semibold text-foreground cursor-pointer"
  onClick={() => handleSort("applicationDeadline")}
>
  <div className="flex items-center gap-1">
    Deadline
    <SortIcon column="applicationDeadline" />
  </div>
</th>
                  {/* <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Status
                  </th> */}

                  <th
  className="text-left px-4 py-3 text-sm font-semibold text-foreground cursor-pointer"
  onClick={() => handleSort("status")}
>
  <div className="flex items-center gap-1">
    Status
    <SortIcon column="status" />
  </div>
</th>
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    No. Of Applicants
                  </th>
                  {/* <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Visibility
                  </th> */}
                  <th className='text-left px-4 py-3 text-sm font-semibold text-foreground'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isPending ? (
                  <tr>
                    <td colSpan='7' className='text-center py-8'>
                      <div className='flex items-center justify-center'>
                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
                      </div>
                    </td>
                  </tr>
                ) : jobs.length === 0 ? (
                  <tr>
                    <td
                      colSpan='7'
                      className='text-center py-8 text-muted-foreground'
                    >
                      No jobs found
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr
                      key={job._id}
                      className='border-b border-border hover:bg-muted/30 transition-colors'
                    >
                      <td className='px-4 py-3'>
                        <div>
                          <p className='font-medium text-foreground'>
                            {job.jobTitle}
                          </p>
                          <p className='text-xs text-muted-foreground'>
                            {job.compensation
                              ? `${job.compensation} (${job.currency})`
                              : 'Not specified'}
                          </p>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <div>
                          <p className='text-sm text-foreground'>
                            {job.sellerId?.firstName} {job.sellerId?.lastName}
                          </p>
                          <p className='text-xs text-muted-foreground'>
                            {job.sellerId?.email}
                          </p>
                        </div>
                      </td>
                       <td className='px-4 py-3'>
                        <div>
                          <p className='text-sm text-foreground'>
                            {job.sellerId?.phoneNumber || '--'} 
                          </p>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <p className='text-sm text-foreground'>
                          {job.address?.city
                            ? `${job.address.city}, ${job.address.state || ''}`
                            : 'N/A'}
                        </p>
                      </td>
                      <td className='px-4 py-3'>
                        <p className='text-sm text-foreground'>
                          {job.applicationDeadline
                            ? new Date(
                                job.applicationDeadline,
                              ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : 'N/A'}
                        </p>
                      </td>
                      <td className='px-4 py-3'>
                       <td className='px-4 py-3'>
                          <button
                            onClick={() => handleStatusToggle(job)}
                            disabled={isUpdating}
                            className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 ${getStatusColor(job.status)}`}
                            title={`Click to set ${job.status === 'Active' ? 'Inactive' : 'Active'}`}
                          >
                            {job.status}
                          </button>
                        </td>
                      </td>

                       <td className='px-4 py-3'>
                        <div>
                          <p className='text-sm text-foreground'>
                            {job?.applicationCount || 0} 
                          </p>
                        </div>
                      </td>

                      
                      {/* <td className='px-4 py-3'>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getVisibilityColor(job.visibility)}`}
                        >
                          {job.visibility}
                        </span>
                      </td> */}
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => handleView(job)}
                            className='p-1.5 hover:text-primary transition-colors'
                            title='View'
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(job)}
                            className='p-1.5 hover:text-primary transition-colors'
                            title='Edit'
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleViewApplicants(job)}
                            className='p-1.5 hover:text-primary transition-colors'
                            title='View Applicants'
                          >
                            <Users size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(job)}
                            className='p-1.5 hover:text-primary transition-colors'
                            title='Delete'
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className='flex items-center justify-between px-4 py-3 border-t border-border'>
              <div className='text-sm text-muted-foreground'>
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{' '}
                of {pagination.total} jobs
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <ChevronLeft size={16} />
                </button>

                <div className='flex items-center space-x-1'>
                  {[...Array(pagination.totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    const current = pagination.page;

                    // Show first, last, current, and adjacent pages
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= current - 1 && pageNum <= current + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                            pageNum === current
                              ? 'bg-primary text-primary-foreground font-medium'
                              : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === current - 2 ||
                      pageNum === current + 2
                    ) {
                      return (
                        <span key={pageNum} className='text-muted-foreground'>
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ViewJobModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        job={selectedJob}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        job={selectedJob}
      />
    </Layout>
  );
};

export default JobList;
